// SkillDataConfig.ts - Configuration and utilities for skill data structures

import {
  Code,
  Users,
  Settings,
  TrendingUp,
  Lightbulb,
  Rocket,
  Database,
  Cloud,
  Smartphone,
  Shield,
  Network,
  MessageCircle,
  GraduationCap,
  Handshake,
  LucideIcon
} from 'lucide-react';

// Type definitions
export interface SkillCategory {
  key: string;
  label: string;
  icon: LucideIcon;
  color: string;
  description: string;
}

export interface SkillWithValue extends SkillCategory {
  value: number;
}

export interface SkillLevel {
  label: string;
  color: string;
  description: string;
}

export interface RoleSkillSet {
  name: string;
  skills: Record<string, SkillWithValue>;
}

export interface SkillRecommendation {
  skill: string;
  current: number;
  target: number;
  priority: 'high' | 'medium' | 'low';
  description: string;
  gap?: number;
}

export interface ExportedSkillsData {
  skills: Array<{
    key: string;
    label: string;
    value: number;
    color: string;
    description: string;
  }>;
  overallScore: number;
  timestamp: string;
  version: string;
}

// Predefined skill categories with icons and colors
export const SKILL_CATEGORIES: Record<string, SkillCategory> = {
  TECHNICAL: {
    key: 'technical',
    label: 'Technical',
    icon: Code,
    color: '#1976D2',
    description: 'Programming, algorithms, and technical problem-solving'
  },
  SOFT_SKILLS: {
    key: 'softSkills',
    label: 'Soft Skills',
    icon: Users,
    color: '#4CAF50',
    description: 'Communication, teamwork, and interpersonal skills'
  },
  DEVOPS: {
    key: 'devops',
    label: 'DevOps',
    icon: Settings,
    color: '#FF9800',
    description: 'CI/CD, infrastructure, and deployment automation'
  },
  ANALYTICS: {
    key: 'analytics',
    label: 'Analytics',
    icon: TrendingUp,
    color: '#9C27B0',
    description: 'Data analysis, metrics, and business intelligence'
  },
  INNOVATION: {
    key: 'innovation',
    label: 'Innovation',
    icon: Lightbulb,
    color: '#F44336',
    description: 'Creative thinking, problem-solving, and innovation'
  },
  LEADERSHIP: {
    key: 'leadership',
    label: 'Leadership',
    icon: Rocket,
    color: '#00BCD4',
    description: 'Team leadership, project management, and vision'
  },
  DATABASE: {
    key: 'database',
    label: 'Database',
    icon: Database,
    color: '#795548',
    description: 'Database design, optimization, and management'
  },
  CLOUD: {
    key: 'cloud',
    label: 'Cloud',
    icon: Cloud,
    color: '#607D8B',
    description: 'Cloud platforms, services, and architecture'
  },
  MOBILE: {
    key: 'mobile',
    label: 'Mobile',
    icon: Smartphone,
    color: '#E91E63',
    description: 'Mobile app development and responsive design'
  },
  SECURITY: {
    key: 'security',
    label: 'Security',
    icon: Shield,
    color: '#FF5722',
    description: 'Cybersecurity, authentication, and data protection'
  },
  PROJECT_MANAGEMENT: {
    key: 'projectManagement',
    label: 'Project Management',
    icon: Network,
    color: '#3F51B5',
    description: 'Project planning, execution, and delivery'
  },
  COMMUNICATION: {
    key: 'communication',
    label: 'Communication',
    icon: MessageCircle,
    color: '#8BC34A',
    description: 'Written and verbal communication skills'
  },
  LEARNING: {
    key: 'learning',
    label: 'Learning',
    icon: GraduationCap,
    color: '#FFC107',
    description: 'Continuous learning and skill development'
  },
  COLLABORATION: {
    key: 'collaboration',
    label: 'Collaboration',
    icon: Handshake,
    color: '#009688',
    description: 'Cross-functional collaboration and partnership'
  }
};

// Default skill sets for different roles
export const ROLE_SKILL_SETS: Record<string, RoleSkillSet> = {
  DEVELOPER: {
    name: 'Software Developer',
    skills: {
      technical: { value: 85, ...SKILL_CATEGORIES.TECHNICAL },
      softSkills: { value: 70, ...SKILL_CATEGORIES.SOFT_SKILLS },
      devops: { value: 60, ...SKILL_CATEGORIES.DEVOPS },
      analytics: { value: 65, ...SKILL_CATEGORIES.ANALYTICS },
      innovation: { value: 75, ...SKILL_CATEGORIES.INNOVATION },
      leadership: { value: 50, ...SKILL_CATEGORIES.LEADERSHIP }
    }
  },
  DEVOPS_ENGINEER: {
    name: 'DevOps Engineer',
    skills: {
      technical: { value: 80, ...SKILL_CATEGORIES.TECHNICAL },
      devops: { value: 90, ...SKILL_CATEGORIES.DEVOPS },
      cloud: { value: 85, ...SKILL_CATEGORIES.CLOUD },
      security: { value: 75, ...SKILL_CATEGORIES.SECURITY },
      softSkills: { value: 70, ...SKILL_CATEGORIES.SOFT_SKILLS },
      leadership: { value: 60, ...SKILL_CATEGORIES.LEADERSHIP }
    }
  },
  PRODUCT_MANAGER: {
    name: 'Product Manager',
    skills: {
      leadership: { value: 85, ...SKILL_CATEGORIES.LEADERSHIP },
      communication: { value: 90, ...SKILL_CATEGORIES.COMMUNICATION },
      analytics: { value: 80, ...SKILL_CATEGORIES.ANALYTICS },
      innovation: { value: 85, ...SKILL_CATEGORIES.INNOVATION },
      softSkills: { value: 85, ...SKILL_CATEGORIES.SOFT_SKILLS },
      technical: { value: 50, ...SKILL_CATEGORIES.TECHNICAL }
    }
  },
  DATA_SCIENTIST: {
    name: 'Data Scientist',
    skills: {
      analytics: { value: 95, ...SKILL_CATEGORIES.ANALYTICS },
      technical: { value: 85, ...SKILL_CATEGORIES.TECHNICAL },
      database: { value: 80, ...SKILL_CATEGORIES.DATABASE },
      innovation: { value: 80, ...SKILL_CATEGORIES.INNOVATION },
      softSkills: { value: 70, ...SKILL_CATEGORIES.SOFT_SKILLS },
      communication: { value: 75, ...SKILL_CATEGORIES.COMMUNICATION }
    }
  },
  UX_DESIGNER: {
    name: 'UX Designer',
    skills: {
      innovation: { value: 90, ...SKILL_CATEGORIES.INNOVATION },
      communication: { value: 85, ...SKILL_CATEGORIES.COMMUNICATION },
      softSkills: { value: 85, ...SKILL_CATEGORIES.SOFT_SKILLS },
      collaboration: { value: 80, ...SKILL_CATEGORIES.COLLABORATION },
      technical: { value: 60, ...SKILL_CATEGORIES.TECHNICAL },
      analytics: { value: 70, ...SKILL_CATEGORIES.ANALYTICS }
    }
  },
  TECH_LEAD: {
    name: 'Tech Lead',
    skills: {
      technical: { value: 90, ...SKILL_CATEGORIES.TECHNICAL },
      leadership: { value: 85, ...SKILL_CATEGORIES.LEADERSHIP },
      devops: { value: 75, ...SKILL_CATEGORIES.DEVOPS },
      communication: { value: 80, ...SKILL_CATEGORIES.COMMUNICATION },
      innovation: { value: 80, ...SKILL_CATEGORIES.INNOVATION },
      softSkills: { value: 75, ...SKILL_CATEGORIES.SOFT_SKILLS }
    }
  }
};

// Skill level descriptions
export const SKILL_LEVELS: Record<number, SkillLevel> = {
  0: { label: 'Beginner', color: '#f44336', description: 'Just starting out' },
  20: { label: 'Novice', color: '#ff9800', description: 'Basic understanding' },
  40: { label: 'Intermediate', color: '#ffc107', description: 'Competent level' },
  60: { label: 'Advanced', color: '#4caf50', description: 'Skilled practitioner' },
  80: { label: 'Expert', color: '#2196f3', description: 'Highly proficient' },
  100: { label: 'Master', color: '#9c27b0', description: 'Exceptional mastery' }
};

// Utility functions
export const getSkillLevel = (value: number): SkillLevel => {
  const levels = Object.keys(SKILL_LEVELS).map(Number).sort((a, b) => b - a);
  for (const level of levels) {
    if (value >= level) {
      return SKILL_LEVELS[level];
    }
  }
  return SKILL_LEVELS[0];
};

export const createCustomSkillSet = (
  skillKeys: string[],
  values: Record<string, number> = {}
): Record<string, SkillWithValue> => {
  const skills: Record<string, SkillWithValue> = {};
  skillKeys.forEach((key) => {
    const category = Object.values(SKILL_CATEGORIES).find(cat => cat.key === key);
    if (category) {
      skills[key] = {
        value: values[key] || 50,
        ...category
      };
    }
  });
  return skills;
};

export const calculateSkillScore = (skills: Record<string, SkillWithValue>): number => {
  const values = Object.values(skills).map(skill => skill.value);
  return Math.round(values.reduce((sum, value) => sum + value, 0) / values.length);
};

export const getSkillRecommendations = (
  skills: Record<string, SkillWithValue>,
  targetRole: string | null = null
): SkillRecommendation[] => {
  const recommendations: SkillRecommendation[] = [];

  // Find skills below 60%
  Object.entries(skills).forEach(([_key, skill]) => {
    if (skill.value < 60) {
      recommendations.push({
        skill: skill.label,
        current: skill.value,
        target: 70,
        priority: skill.value < 40 ? 'high' : 'medium',
        description: skill.description
      });
    }
  });

  // If target role is specified, compare with role requirements
  if (targetRole && ROLE_SKILL_SETS[targetRole]) {
    const targetSkills = ROLE_SKILL_SETS[targetRole].skills;
    Object.entries(targetSkills).forEach(([key, targetSkill]) => {
      const currentSkill = skills[key];
      if (currentSkill && currentSkill.value < targetSkill.value - 10) {
        recommendations.push({
          skill: currentSkill.label,
          current: currentSkill.value,
          target: targetSkill.value,
          priority: 'high',
          description: `Required for ${targetRole} role`,
          gap: targetSkill.value - currentSkill.value
        });
      }
    });
  }

  return recommendations.sort((a, b) => {
    const priorityOrder: Record<string, number> = { high: 3, medium: 2, low: 1 };
    return priorityOrder[b.priority] - priorityOrder[a.priority];
  });
};

export const exportSkillsData = (skills: Record<string, SkillWithValue>): ExportedSkillsData => {
  return {
    skills: Object.entries(skills).map(([key, skill]) => ({
      key,
      label: skill.label,
      value: skill.value,
      color: skill.color,
      description: skill.description
    })),
    overallScore: calculateSkillScore(skills),
    timestamp: new Date().toISOString(),
    version: '1.0'
  };
};

export const importSkillsData = (data: ExportedSkillsData | null): Record<string, SkillWithValue> => {
  if (!data?.skills) {
    throw new Error('Invalid skills data format');
  }
  const skills: Record<string, SkillWithValue> = {};
  data.skills.forEach(skillData => {
    const category = Object.values(SKILL_CATEGORIES).find(cat => cat.key === skillData.key);
    if (category) {
      skills[skillData.key] = {
        value: skillData.value || 50,
        ...category
      };
    }
  });

  return skills;
};

// Default export for easy importing
const skillDataConfigExports = {
  SKILL_CATEGORIES,
  ROLE_SKILL_SETS,
  SKILL_LEVELS,
  getSkillLevel,
  createCustomSkillSet,
  calculateSkillScore,
  getSkillRecommendations,
  exportSkillsData,
  importSkillsData
};

export default skillDataConfigExports;
